<script setup>
/* Transfers-Seite ("AFH Transfers"): alle transferierten Spieler, immer offen. Die Suche (oben, dieselbe wie
   auf der Kaderseite) gilt für diese Seite. Positionsfilter und Sortierung (Position, A–Z, Age, Transfer
   date) haben einen eigenen Zustand (transferView in usePlayers.js), unabhängig von der
   Kaderseite; sie liegen hinter dem Knopf "Filter" (FilterToggle/FilterBox, dieselben wie auf der Kaderseite).
   Gruppierung und Reihenfolge: domain/squadPages.js transferSections. Keine Jahrgangsknöpfe. Die Zahl
   steht wie auf der Kaderseite unter der Suche (F20); Überschrift und Beschreibung nur für Vorleser. */
import { computed, ref } from 'vue';
import { usePlayers } from '../composables/usePlayers.js';
import { useTransferDates } from '../composables/useTransfers.js';
import { transferSections } from '../domain/squadPages.js';
import { ALL, filterSquad, positionCountsFor, activeFilterCount } from '../domain/squadFilter.js';
import FilterToggle from './FilterToggle.vue';
import FilterBox from './FilterBox.vue';
import { flattenSections } from '../domain/profileNav.js';
import SquadSearch from './SquadSearch.vue';
import ListControls from './ListControls.vue';
import ListSections from './ListSections.vue';
import TransferredRow from './TransferredRow.vue';
import PlayerColumn from './PlayerColumn.vue';

const { transferred, transferredMatching, transferView, query, loading, error, open } = usePlayers();
const { positionFilter, sortMode, ageDirection } = transferView;

/* Datum für ALLE Transferierten laden (nicht nur die Treffer), damit die Reihenfolge beim Tippen nicht wartet. */
const { dateOf } = useTransferDates(transferred);

/* Filter-Bereich: beim Laden zu, Zustand wird nicht gespeichert (wie auf der Kaderseite) */
const filtersOpen = ref(false);
const FILTER_PANEL_ID = 'transferred-filter-panel';

/* Zähler folgen der Suche und dem jeweils anderen Filter, Liste allen Filtern (UND) */
const positionCounts = computed(() => positionCountsFor(transferredMatching.value));
const filtered = computed(() => filterSquad(transferredMatching.value, { position: positionFilter.value }));
/* Keine Altersklasse auf dieser Seite (nicht nötig für Transferierte) */
const activeCount = computed(() => activeFilterCount({ position: positionFilter.value }));
/** Position zurück auf "alle"; Suche und Sortierung bleiben */
function resetFilters() {
  positionFilter.value = ALL;
}
const sections = computed(() =>
  transferSections(filtered.value, { sort: sortMode.value, direction: ageDirection.value, dateOf })
);

/* Profil öffnen und die angezeigte Reihenfolge dieser Seite als Kontext mitgeben (domain/profileNav.js) */
const openFromList = (id) => open(id, null, flattenSections(sections.value));
</script>

<template>
  <div id="transferred-area">
    <div class="branch" aria-hidden="true"></div>

    <SquadSearch>
      <FilterToggle v-model="filtersOpen" :count="activeCount" :panel-id="FILTER_PANEL_ID" />
      <!-- Spielerzahl wie auf der Kaderseite unter der Suche (Handy) bzw. rechts in der Zeile (Laptop) -->
      <div class="list-meta">
        <span class="shown-count" data-testid="shown-count" aria-live="polite">{{ $t('squad.shownCount', { count: filtered.length }, filtered.length) }}</span>
      </div>
    </SquadSearch>

    <!-- Derselbe Filter-Bereich wie auf der Kaderseite; ohne Status (alle sind transferiert) und ohne Jahrgang -->
    <FilterBox :open="filtersOpen" :count="activeCount" :id="FILTER_PANEL_ID" @reset="resetFilters">
      <ListControls
        v-model:position="positionFilter"
        v-model:sort="sortMode"
        v-model:direction="ageDirection"
        :counts="positionCounts"
        show-date
      />
    </FilterBox>

    <div class="branches">
      <div v-if="error" class="empty-col error">{{ $t(`errors.${error}`) }}</div>
      <div v-else-if="loading" class="empty-col">{{ $t('common.loading') }}</div>

      <PlayerColumn
        v-else
        :title="$t('squadPages.transferredTitle')"
        :description="$t('squad.transferredDesc')"
        :count="filtered.length"
        sr-only-head
      >
        <div v-if="!filtered.length" class="empty-col" data-testid="transferred-empty">
          {{ transferred.length ? $t('squad.empty') : $t('squadPages.transferredEmpty') }}
        </div>
        <ListSections :sections="sections" :sort="sortMode">
          <template #member="{ player }">
            <TransferredRow :player="player" @open="openFromList" />
          </template>
        </ListSections>
      </PlayerColumn>
    </div>
  </div>
</template>

<style scoped>
/* Spielerzahl wie in SquadList.vue (gleiche Werte; die Kaderseite bleibt unangetastet) */
.list-meta {
  flex: 1 1 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}
.shown-count {
  font-size: var(--fs-xs);
  font-weight: 500;
  color: var(--muted);
  min-width: 0;
  overflow-wrap: anywhere;
}
@media (min-width: 601px) {
  .list-meta {
    display: contents;
  }
  .shown-count {
    order: 2;
    margin-left: auto;
    padding-right: 2px;
    white-space: nowrap;
  }
}
.empty-col.error {
  color: var(--red);
}
</style>
